import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import QRCode from 'react-qr-code';
import LectorQRCredencial from './LectorQRCredencial';
import AfiliadosSolicitud from './AfiliadosSolicitud';
import { motion } from "motion/react";

const SUPABASE_URL = 'https://mdftijdjckugrdouslni.supabase.co';
const SUPABASE_KEY = 'sb_publishable_itbwTGCKUHx5Kg_FuYfT8g_iiZsVnUS';

const compressImage = (file, maxWidth, maxHeight, quality = 0.8) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Error al convertir la imagen a WebP'));
                        }
                    },
                    'image/webp',
                    quality
                );
            };
            img.onerror = (error) => reject(error);
        };
        reader.onerror = (error) => reject(error);
    });
};

function LogPanel({ logs }) {
    const [isOpen, setIsOpen] = useState(true);
    if (!logs || logs.length === 0 || !isOpen) return null;
    return (
        <div className="fixed bottom-0 left-0 w-full bg-gray-900 border-t border-gray-700 text-green-400 font-mono text-xs p-3 h-40 overflow-y-auto z-[9999] opacity-95">
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-gray-300 font-bold uppercase text-[10px] tracking-wider">Log Panel (Admin Mode)</h3>
                <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white flex items-center bg-transparent border-none p-1 cursor-pointer">
                    <span className="material-icons text-sm">close</span>
                </button>
            </div>
            {logs.map((log, i) => (
                <div key={i} className="mb-1 leading-tight">{log}</div>
            ))}
        </div>
    );
}

const SupasoLogoAnimated = ({ size = "normal" }) => {
    const isSmall = size === "small";
    const scale = isSmall ? 0.6 : 1;
    return (
        <div className={`relative flex items-center justify-center ${isSmall ? 'h-24' : 'mb-6'}`}>
            <div className="relative flex items-center justify-center" style={{ transform: `scale(${scale})` }}>
                <motion.div
                    className="absolute w-[103px] h-[103px] border-[7px] border-transparent border-t-[#00A400] border-r-[#00A400] border-b-[#00A400] rounded-full"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                />
                <motion.div
                    className="absolute w-[86px] h-[86px] border-[7px] border-transparent border-t-[#FD3033] border-l-[#FD3033] border-b-[#FD3033] rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                />
                <div className="relative z-10 flex items-center justify-center bg-white rounded-full p-1 shadow-sm">
                    <img src="https://supaso.org/logo.png" alt="Logo" className="w-[67px] h-[67px] object-contain" referrerPolicy="no-referrer" />
                </div>
            </div>
        </div>
    );
};

export default function App() {
    const [currentScreen, setCurrentScreen] = useState('loading');
    const [logs, setLogs] = useState([]);
    const [currentAfiliado, setCurrentAfiliado] = useState(null);

    useEffect(() => {
        // Restaurar sesión desde localStorage para no obligar a re-loguear siempre
        const savedAfi = localStorage.getItem('supaso_afiliado');
        if (savedAfi) {
            try {
                const parsed = JSON.parse(savedAfi);
                if (parsed && parsed.id) {
                    setCurrentAfiliado(parsed);
                    setCurrentScreen('welcome');
                    addLog(`🔄 Sesión restaurada localmente para ${parsed.nombre}`);
                } else {
                    setCurrentScreen('login');
                }
            } catch (e) {
                localStorage.removeItem('supaso_afiliado');
                setCurrentScreen('login');
            }
        } else {
            setCurrentScreen('login');
        }
    }, []);

    const addLog = (msg) => {
        console.log(msg);
        setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`].slice(-50));
    };

    const handleLoginSuccess = (afiliado) => {
        setCurrentAfiliado(afiliado);
        localStorage.setItem('supaso_afiliado', JSON.stringify(afiliado));
        setCurrentScreen('welcome');
    };

    const handleLogout = () => {
        setCurrentAfiliado(null);
        localStorage.removeItem('supaso_afiliado');
        setCurrentScreen('login');
    };

    return (
        <div className="min-h-screen flex flex-col">
            {currentScreen === 'loading' && (
                <div className="min-h-screen flex flex-col items-center justify-center bg-background-light dark:bg-background-dark p-6">
                    <SupasoLogoAnimated />
                    <div className="mt-8 space-y-4 text-center">
                        <p className="text-[#012160] dark:text-white font-bold animate-pulse text-lg tracking-widest uppercase">Validando Acceso</p>
                        <div className="flex items-center justify-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#0086CE] animate-bounce" style={{ animationDelay: '0ms' }}></span>
                            <span className="w-1.5 h-1.5 rounded-full bg-[#0086CE] animate-bounce" style={{ animationDelay: '200ms' }}></span>
                            <span className="w-1.5 h-1.5 rounded-full bg-[#0086CE] animate-bounce" style={{ animationDelay: '400ms' }}></span>
                        </div>
                    </div>
                </div>
            )}
            {currentScreen === 'login' && <LoginScreen onLoginSuccess={handleLoginSuccess} onNavigate={setCurrentScreen} addLog={addLog} />}
            {currentScreen === 'welcome' && <WelcomeScreen afiliado={currentAfiliado} onNavigate={setCurrentScreen} onLogout={handleLogout} />}
            {currentScreen === 'credential' && <CredentialScreen afiliado={currentAfiliado} onNavigate={setCurrentScreen} addLog={addLog} onLogout={handleLogout} />}
            {currentScreen === 'beneficios' && <BeneficiosScreen onNavigate={setCurrentScreen} addLog={addLog} />}
            {currentScreen === 'scanner' && <LectorQRCredencial onBack={() => setCurrentScreen('login')} />}
            <LogPanel logs={logs} />
        </div>
    );
}

function WelcomeScreen({ afiliado, onNavigate, onLogout }) {
    const [greeting, setGreeting] = useState('');
    const [quote, setQuote] = useState('');

    useEffect(() => {
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 12) setGreeting('¡Buen día');
        else if (hour >= 12 && hour < 20) setGreeting('¡Buenas tardes');
        else setGreeting('¡Buenas noches');

        const quotes = [
            "La seguridad es el motor de la excelencia profesional.",
            "Tu compromiso hoy construye el bienestar de mañana.",
            "Un profesional protegido es un profesional imparable.",
            "Grandes metas requieren grandes protecciones.",
            "En SUPASO, tu futuro está en las mejores manos.",
            "La prevención es la clave del éxito sostenido.",
            "Juntos hacemos de la seguridad un estándar de vida."
        ];
        const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
        setQuote(quotes[dayOfYear % quotes.length]);
    }, []);

    return (
        <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col items-center justify-center py-4 px-4 relative overflow-hidden transition-colors duration-300 text-center font-body">
            {/* Fondo decorativo premium con mayor contraste */}
            <div className="absolute top-[-5%] left-[-10%] w-[45%] h-[45%] bg-blue-100/60 dark:bg-blue-900/30 rounded-full blur-3xl animate-blob"></div>
            <div className="absolute bottom-[-5%] right-[-10%] w-[45%] h-[45%] bg-emerald-100/60 dark:bg-emerald-900/30 rounded-full blur-3xl animate-blob animation-delay-2000"></div>

            <div className="w-full max-w-[390px] z-10 transition-all">
                <div className="bg-white dark:bg-surface-dark rounded-[3rem] shadow-[0_30px_70px_rgba(1,33,96,0.25)] dark:shadow-[0_30px_70px_rgba(0,0,0,0.5)] border border-slate-200 dark:border-slate-800 p-8 flex flex-col items-center space-y-6 min-h-[690px] justify-between transition-all">
                    <div className="w-full space-y-6 flex flex-col items-center">
                        <div className="flex justify-center">
                            <SupasoLogoAnimated />
                        </div>

                        <div className="space-y-2 w-full">
                            <h2 className="text-3xl font-black text-[#012160] dark:text-white leading-tight">
                                {greeting},<br />
                                <span className="text-[#0086CE] font-hand text-4xl block mt-1 tracking-tight">{afiliado?.nombre?.split(' ')[0]}</span>
                            </h2>
                            <div className="relative py-4 flex flex-col items-center">
                                <span className="material-icons text-[#0086CE]/30 dark:text-slate-700 text-4xl absolute top-0 left-4 select-none">format_quote</span>
                                <p className="text-slate-600 dark:text-slate-300 font-medium text-xl px-10 font-script leading-relaxed relative z-10 italic">
                                    {quote}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 w-full">
                        <button
                            onClick={() => onNavigate('credential')}
                            className="flex items-center gap-4 p-4.5 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800 text-left hover:border-[#0086CE] hover:bg-white dark:hover:bg-slate-900 transition-all group w-full shadow-sm"
                        >
                            <div className="w-11 h-11 rounded-xl bg-[#0086CE] flex items-center justify-center text-white shadow-lg shadow-blue-500/20">
                                <span className="material-icons-round text-2xl">badge</span>
                            </div>
                            <div className="flex-1">
                                <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em] mb-0.5">Gestión 01</p>
                                <h4 className="font-bold text-sm text-slate-700 dark:text-white uppercase transition-colors group-hover:text-[#0086CE]">Mi Credencial</h4>
                            </div>
                            <span className="material-icons-round text-slate-300 transition-transform group-hover:translate-x-1 text-base">chevron_right</span>
                        </button>

                        <button
                            onClick={() => onNavigate('beneficios')}
                            className="flex items-center gap-4 p-4.5 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800 text-left hover:border-emerald-500 hover:bg-white dark:hover:bg-slate-900 transition-all group w-full shadow-sm"
                        >
                            <div className="w-11 h-11 rounded-xl bg-emerald-500 flex items-center justify-center text-white shadow-lg shadow-emerald-500/20">
                                <span className="material-icons-round text-2xl">local_activity</span>
                            </div>
                            <div className="flex-1">
                                <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em] mb-0.5">Gestión 02</p>
                                <h4 className="font-bold text-sm text-slate-700 dark:text-white uppercase transition-colors group-hover:text-emerald-500">Red de Beneficios</h4>
                            </div>
                            <span className="material-icons-round text-slate-300 transition-transform group-hover:translate-x-1 text-base">chevron_right</span>
                        </button>
                    </div>

                    <div className="pt-4 border-t border-slate-100 dark:border-slate-800 w-full space-y-6">
                        <div className="text-left px-2">
                            <h4 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] mb-2">Novedades y Normativa</h4>
                            <a
                                href="https://www.argentina.gob.ar/normativa/nacional/ley-19587-17612"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex flex-col gap-1.5 p-4 bg-blue-50/50 dark:bg-blue-900/10 rounded-2xl border border-transparent hover:border-blue-200 dark:hover:border-blue-800 transition-all group w-full shadow-sm"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-bold text-[#0086CE] uppercase tracking-wider">Ley Higiene y Seguridad</span>
                                    <span className="material-icons text-sm text-[#0086CE] group-hover:rotate-45 transition-transform">north_east</span>
                                </div>
                                <p className="text-[12px] font-bold text-slate-700 dark:text-slate-200 leading-snug">
                                    Normativa nacional (Ley 19587/1972): Bases del sistema preventivo en Argentina.
                                </p>
                            </a>
                        </div>
                        {/* Cerrar Sesión integrado en la tarjeta para ahorro de espacio */}
                        <button
                            onClick={onLogout}
                            className="flex items-center justify-center gap-2 w-full pt-4 text-slate-400 dark:text-slate-500 hover:text-red-500 transition-all border-t border-slate-50 dark:border-slate-800/20 group"
                        >
                            <span className="material-icons-round text-xl transition-transform group-hover:-translate-x-1 opacity-70">logout</span>
                            <span className="font-bold uppercase text-[11px] tracking-[0.2em]">Cerrar Sesión</span>
                        </button>
                    </div>

                    {/* Footer corporativo interno */}
                    <div className="w-full pt-4 text-center">
                        <p style={{ fontSize: '9px', color: '#00A400', fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em' }} className="font-medium">
                            © {new Date().getFullYear()} - <span style={{ fontFamily: '"Russo One", sans-serif', fontWeight: 500, color: '#0086CE' }}>SUPASO</span> <span style={{ color: '#0086CE', fontWeight: 700 }}>Tecnología</span> - v3.0
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function LoginScreen({ onLoginSuccess, onNavigate, addLog }) {
    const [activeTab, setActiveTab] = useState('ingresar');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showPhone, setShowPhone] = useState(false);
    const [showEmail, setShowEmail] = useState(false);
    const [showDni, setShowDni] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        addLog("Intentando acceso...");

        const formData = new FormData(e.target);
        const dni = formData.get('dni').replace(/\D/g, '');
        const phone = formData.get('phone').replace(/\D/g, '');
        const email = formData.get('email').trim().toLowerCase();

        try {
            const { data, error: supaError } = await supabase
                .from('afiliados')
                .select('*')
                .eq('dni', dni)
                .limit(1);

            if (supaError) throw supaError;

            if (!data || data.length === 0) {
                addLog(`❌ DNI ${dni} no encontrado.`);
                throw new Error("El DNI ingresado no está registrado.");
            }

            const afi = data[0];

            // Validar teléfono (limpiando formatos para comparar)
            const dbPhone = String(afi.telefono || '').replace(/\D/g, '');
            const dbEmail = String(afi.email || afi.correo || '').trim().toLowerCase();

            if (dbPhone !== phone.replace(/\D/g, '')) {
                addLog(`❌ Teléfono incorrecto.`);
                throw new Error("El teléfono no coincide con el registrado.");
            }

            if (dbEmail !== email) {
                addLog(`❌ Correo incorrecto.`);
                throw new Error("El correo electrónico no coincide con el registrado.");
            }

            const status = String(afi.estado || '').toLowerCase();
            const caracter = String(afi.caracter_afiliado || '').toUpperCase();

            if (caracter !== 'HONORARIO' && status !== 'aprobado' && status !== 'vigente') {
                const statusLabel = status === 'pendiente' ? 'PENDIENTE' : (status === 'rechazado' ? 'RECHAZADA' : status.toUpperCase());
                addLog(`⚠️ Estado ${statusLabel} para DNI ${dni}`);
                throw new Error(`Tu solicitud está en estado: ${statusLabel}.`);
            }

            addLog(`✅ Acceso concedido: ${afi.nombre}`);
            onLoginSuccess(afi);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-background-light dark:bg-background-dark font-body min-h-screen flex flex-col items-center justify-center py-2 px-6 transition-colors duration-300 relative overflow-hidden">
            {/* Fondo decorativo premium para coherencia con el Dashboard */}
            <div className="absolute top-[-5%] left-[-10%] w-[45%] h-[45%] bg-blue-100/40 dark:bg-blue-900/20 rounded-full blur-3xl animate-blob"></div>
            <div className="absolute bottom-[-5%] right-[-10%] w-[45%] h-[45%] bg-emerald-100/40 dark:bg-emerald-900/20 rounded-full blur-3xl animate-blob animation-delay-2000"></div>

            <div className="w-full max-w-[390px] bg-white dark:bg-surface-dark shadow-[0_30px_70px_rgba(1,33,96,0.2)] dark:shadow-[0_30px_70px_rgba(0,0,0,0.4)] rounded-[3rem] border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col relative transition-all duration-300 min-h-[640px] z-10">
                <div className="pt-6 pb-2 px-8 flex flex-col items-center justify-center text-center">
                    <h2 className="font-brand text-2xl text-[#012160] dark:text-white mb-1 tracking-[0.25em]">SUPASO</h2>
                    <div
                        className="scale-90 origin-center my-1 -mb-1 cursor-pointer"
                        onClick={(e) => {
                            if (e.shiftKey) {
                                const emailInput = document.getElementById('email');
                                if (emailInput && emailInput.value) {
                                    addLog(`⚡ BYPASS: Entrando como ${emailInput.value.trim().toLowerCase()}`);
                                    supabase.from('afiliados').select('*').eq('email', emailInput.value.trim().toLowerCase()).limit(1).then(({ data }) => {
                                        if (data && data[0]) onLoginSuccess(data[0]);
                                    });
                                }
                            }
                        }}
                    >
                        <SupasoLogoAnimated />
                    </div>
                    <h1 className="text-[13px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-2 flex items-center justify-center gap-2">
                        Portal de Afiliados
                        <button type="button" onClick={() => onNavigate('scanner')} className="text-slate-300 dark:text-slate-600 hover:text-[#0086CE] transition-colors bg-transparent border-none p-0 cursor-pointer flex items-center" title="Validar Código QR">
                            <span className="material-icons text-xl leading-none">qr_code_scanner</span>
                        </button>
                    </h1>
                </div>
                <div className="flex w-full border-b border-slate-100 dark:border-slate-800">
                    <button
                        className={`flex-1 pb-4 pt-2 text-center font-bold transition-all focus:outline-none text-[13px] uppercase tracking-wider border-b-2 ${activeTab === 'ingresar' ? 'text-[#0086CE] border-[#0086CE]' : 'text-slate-400 dark:text-slate-500 border-transparent hover:text-slate-600 dark:hover:text-slate-300'}`}
                        onClick={() => setActiveTab('ingresar')}
                    >Ingresar</button>
                    <button
                        className={`flex-1 pb-4 pt-2 text-center font-bold transition-all focus:outline-none text-[13px] uppercase tracking-wider border-b-2 ${activeTab === 'nuevo' ? 'text-[#0086CE] border-[#0086CE]' : 'text-slate-400 dark:text-slate-500 border-transparent hover:text-slate-600 dark:hover:text-slate-300'}`}
                        onClick={() => { setActiveTab('nuevo'); setError(null); }}
                    >Nuevo Afiliado</button>
                </div>
                <div className="p-8 flex-1 flex flex-col justify-center">
                    {activeTab === 'ingresar' && (
                        <div className="space-y-4">
                            {error && (
                                <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-3 rounded-r text-[11px] text-red-800 dark:text-red-200 mb-1 leading-tight">
                                    <p>⚠️ {error}</p>
                                </div>
                            )}
                            {!error && (
                                <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-[#0086CE] p-3 rounded-r text-[11px] text-blue-800 dark:text-blue-200 leading-tight">
                                    <p>Ingrese los datos para acceder a su Credencial Digital.</p>
                                </div>
                            )}
                            <form className="space-y-3.5" onSubmit={handleLogin}>
                                <div className="space-y-1">
                                    <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 ml-1 uppercase tracking-wider">DNI</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="material-icons text-gray-400 text-base">badge</span>
                                        </div>
                                        <input
                                            className="focus:ring-[#0086CE] focus:border-[#0086CE] block w-full pl-9 pr-10 text-sm border-gray-300 dark:border-gray-600 rounded-lg py-2.5 dark:bg-gray-800 dark:text-white placeholder-gray-400"
                                            id="dni"
                                            maxLength="8"
                                            name="dni"
                                            placeholder="Ej: 12345678"
                                            type={showDni ? "text" : "password"}
                                            required
                                            disabled={loading}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowDni(!showDni)}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                        >
                                            <span className="material-icons text-lg">{showDni ? 'visibility_off' : 'visibility'}</span>
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 ml-1 uppercase tracking-wider">Teléfono</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="material-icons text-gray-400 text-base">phone_android</span>
                                        </div>
                                        <input
                                            className="focus:ring-[#0086CE] focus:border-[#0086CE] block w-full pl-9 pr-10 text-sm border-gray-300 dark:border-gray-600 rounded-lg py-2.5 dark:bg-gray-800 dark:text-white placeholder-gray-400"
                                            id="phone"
                                            maxLength="10"
                                            name="phone"
                                            placeholder="Ej: 1122334455"
                                            type={showPhone ? "text" : "password"}
                                            required
                                            disabled={loading}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPhone(!showPhone)}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                        >
                                            <span className="material-icons text-lg">{showPhone ? 'visibility_off' : 'visibility'}</span>
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 ml-1 uppercase tracking-wider">Correo Electrónico</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="material-icons text-gray-400 text-base">mail</span>
                                        </div>
                                        <input
                                            className="focus:ring-[#0086CE] focus:border-[#0086CE] block w-full pl-9 pr-10 text-sm border-gray-300 dark:border-gray-600 rounded-lg py-2.5 dark:bg-gray-800 dark:text-white placeholder-gray-400"
                                            id="email"
                                            name="email"
                                            placeholder="Ej: juan@gmail.com"
                                            type={showEmail ? "text" : "password"}
                                            required
                                            disabled={loading}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowEmail(!showEmail)}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                        >
                                            <span className="material-icons text-lg">{showEmail ? 'visibility_off' : 'visibility'}</span>
                                        </button>
                                    </div>
                                </div>
                                <div className="pt-2">
                                    <button className="w-full flex justify-center items-center py-4 px-4 border border-transparent rounded-2xl shadow-sm text-sm font-bold text-white bg-[#0086CE] hover:bg-blue-600 focus:outline-none transition-colors transform active:scale-[0.98] disabled:opacity-70" type="submit" disabled={loading}>
                                        {loading ? <div className="animate-spin h-5 w-5 border-2 border-white rounded-full border-t-transparent mr-2"></div> : <span className="material-icons text-lg mr-2">login</span>}
                                        {loading ? 'VALIDANDO...' : 'INGRESAR A MI CREDENCIAL'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                    {activeTab === 'nuevo' && (
                        <div className="flex flex-col items-center justify-center h-full py-4 text-center space-y-6">
                            <div className="h-20 w-20 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-2">
                                <span className="material-icons text-4xl text-[#0086CE]">person_add</span>
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white">¡Bienvenido a SUPASO!</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
                                    Únete a nuestra comunidad de profesionales. Completa el formulario de alta para comenzar a disfrutar de todos los beneficios.
                                </p>
                            </div>
                            <div className="w-full pt-4 space-y-4">
                                <button onClick={() => onNavigate('register')} className="w-full flex justify-center items-center py-3.5 px-4 border-2 border-[#0086CE] rounded-lg shadow-sm text-sm font-bold text-[#0086CE] bg-transparent hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors transform active:scale-[0.98]" type="button">
                                    SOLICITUD DE ALTA
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer corporativo interno */}
                <div className="w-full pb-8 text-center">
                    <p style={{ fontSize: '9px', color: '#00A400', fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em' }} className="font-medium">
                        © {new Date().getFullYear()} - <span style={{ fontFamily: '"Russo One", sans-serif', fontWeight: 500, color: '#0086CE' }}>SUPASO</span> <span style={{ color: '#0086CE', fontWeight: 700 }}>Tecnología</span> - v3.0
                    </p>
                </div>
            </div>
        </div>
    );
}



function SuccessScreen({ onNavigate }) {
    return (
        <div className="font-display bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark min-h-screen flex items-center justify-center p-4">
            <main className="w-full max-w-sm bg-surface-light dark:bg-surface-dark shadow-xl rounded-xl border flex flex-col items-center p-8 text-center transition-colors duration-300">
                <div className="mb-8 w-full flex justify-center"><h1 className="text-3xl font-black uppercase text-gray-900 dark:text-white">SUPASO</h1></div>
                <div className="mb-6 animate-pulse">
                    <div className="rounded-full bg-yellow-100 dark:bg-yellow-900/30 p-4 inline-flex"><span className="material-icons-outlined text-6xl text-yellow-500 dark:text-yellow-400">hourglass_empty</span></div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Solicitud Enviada con Éxito</h2>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-8 text-left border border-blue-100 dark:border-blue-800/30 w-full space-y-2">
                    <div className="flex justify-between">
                        <span className="text-xs text-blue-600 dark:text-blue-300 uppercase font-medium">Estado</span>
                        <span className="text-xs text-yellow-600 dark:text-yellow-400 font-bold bg-white dark:bg-gray-800 px-2 py-1 rounded shadow-sm">En Revisión</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-xs text-blue-600 dark:text-blue-300 uppercase font-medium">Fecha</span>
                        <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">{new Date().toLocaleDateString('es-AR')}</span>
                    </div>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-8 text-sm">Su perfil ha sido registrado y está siendo validado. Recibirá una notificación una vez aprobado por la administración.</p>
                <button onClick={() => onNavigate('login')} className="w-full bg-primary hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md flex items-center justify-center gap-2 group transition-all">
                    <span className="material-icons-outlined text-lg group-hover:-translate-x-1 transition-transform">arrow_back</span>
                    Volver al Inicio
                </button>
            </main>
        </div>
    );
}

function CredentialScreen({ afiliado, onNavigate, addLog, onLogout }) {
    const [qrTime, setQrTime] = useState(Date.now());
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => setQrTime(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);

    if (!afiliado) {
        onLogout();
        return null;
    }

    const fmtDNI = (d) => (d || '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const fotoPerfilUrl = afiliado.url_foto_perfil || afiliado.foto_perfil;
    const status = String(afiliado.estado || '').toLowerCase();
    const caracter = String(afiliado.caracter_afiliado || '').toUpperCase();
    const esVigente = status === 'aprobado' || status === 'vigente' || caracter === 'HONORARIO';

    const qrData = `DNI: ${fmtDNI(afiliado.dni)}\nEstado: ${esVigente ? 'AL DÍA' : 'INHABILITADO'}\nFecha: ${new Date(qrTime).toLocaleDateString('es-AR')} ${new Date(qrTime).toLocaleTimeString('es-AR', { hour12: false })}`;

    return (
        <div className="bg-slate-50 dark:bg-[#020617] font-display h-[100dvh] flex flex-col items-center justify-center p-4 overflow-hidden relative">
            {/* Background Decor */}
            <div className="absolute top-0 left-0 w-full h-full opacity-30 pointer-events-none">
                <div className="absolute top-[-10%] left-[-20%] w-[120%] h-[60%] bg-gradient-to-b from-blue-100 to-transparent dark:from-blue-900/20 rounded-full blur-[120px]"></div>
            </div>

            <div className="w-full max-w-[380px] h-[98vh] flex flex-col items-center bg-white dark:bg-gray-900 shadow-2xl rounded-[3rem] overflow-hidden border border-slate-200/60 dark:border-slate-800 relative z-10 transition-all">

                {/* Header Compacto */}
                <div className="w-full pt-8 pb-4 flex flex-col items-center">
                    <SupasoLogoAnimated size="small" />
                    <h1 style={{ fontFamily: '"Russo One", sans-serif' }} className="text-[#012160] dark:text-white text-2xl uppercase tracking-widest -mt-4">SUPASO</h1>
                </div>

                {/* BANDA PREMIUM (MESH GRADIENT) */}
                <div className="relative w-full flex-none min-h-[180px] bg-gradient-to-br from-[#012160] via-[#0086CE] to-[#012160] px-6 py-4 flex flex-col justify-center overflow-visible">
                    {/* Efecto de brillo táctico */}
                    <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl -mr-10 -mt-10 overflow-hidden"></div>

                    <div className="flex justify-between items-center relative z-10 w-full mb-2">
                        <div className="flex-1 pr-2 max-w-[65%]">
                            <span className="text-blue-100/60 text-[8px] font-black uppercase tracking-[0.2em] block mb-1">Credencial Digital</span>
                            <h2 className="text-white text-xl sm:text-2xl font-black uppercase leading-[1.1] drop-shadow-lg mb-1 truncate">
                                {afiliado.apellido}
                            </h2>
                            <h2 className="text-white text-lg sm:text-xl opacity-90 font-bold uppercase leading-[1.1] truncate">
                                {afiliado.nombre}
                            </h2>
                            <p className="text-blue-100 font-mono font-bold text-sm mt-1 drop-shadow-sm">DNI {fmtDNI(afiliado.dni)}</p>
                        </div>

                        <div className="relative group shrink-0 ml-2">
                            <div className="absolute inset-0 bg-white/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            {/* Eliminado overflow-hidden del div padre que cortaba la imagen, se asegura que img tenga object-cover y rounded-full */}
                            <div className="w-[85px] h-[85px] sm:w-[96px] sm:h-[96px] rounded-full border-[3px] border-white/90 shadow-xl relative z-20 bg-slate-100 flex items-center justify-center -mr-2">
                                {fotoPerfilUrl && !imgError ? (
                                    <img src={fotoPerfilUrl} alt="Foto" className="w-full h-full object-cover rounded-full" onError={() => setImgError(true)} />
                                ) : (
                                    <span className="material-icons text-slate-400 text-5xl">person</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-white/20 relative z-10">
                        <div>
                            <p className="text-blue-100/50 text-[8px] font-black uppercase tracking-widest">Delegación</p>
                            <p className="text-white font-bold text-[11px] sm:text-xs truncate drop-shadow-sm uppercase">{afiliado.delegacion || 'SEDE CENTRAL'}</p>
                        </div>
                        <div>
                            <p className="text-blue-100/50 text-[8px] font-black uppercase tracking-widest">Afiliado</p>
                            <p className="text-white font-bold text-[11px] sm:text-xs truncate drop-shadow-sm uppercase">{afiliado.caracter_afiliado || 'ACTIVO'}</p>
                        </div>
                    </div>
                </div>

                {/* ÁREA QR & ESTADO */}
                <div className="w-full flex-1 flex flex-col items-center justify-center bg-white dark:bg-gray-900 px-6 py-2">
                    <div className={`mb-4 flex items-center gap-2 px-4 py-1.5 rounded-full font-black text-[9px] sm:text-[10px] tracking-[0.1em] shadow-sm uppercase ${esVigente ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                        <span className={`w-2 h-2 rounded-full animate-pulse shrink-0 ${esVigente ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                        <span className="truncate">{esVigente ? 'Habilitado - Al Día' : 'Inhabilitado'}</span>
                    </div>

                    <div className="relative p-4 bg-white dark:bg-slate-800 rounded-3xl shadow-[0_15px_40px_rgba(0,0,0,0.06)] dark:shadow-none border border-slate-100 dark:border-slate-800 group transition-all">
                        <div className="absolute -inset-1 bg-gradient-to-tr from-[#0086CE]/10 to-transparent rounded-[2rem] blur opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative bg-white p-2 rounded-xl">
                            <QRCode value={qrData} size={160} style={{ opacity: esVigente ? 1 : 0.15 }} />
                            {!esVigente && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="material-icons text-7xl text-red-500/80 drop-shadow-lg">block</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="mt-4 text-center">
                        <p className="text-[9px] font-bold text-slate-300 dark:text-slate-600 uppercase tracking-[0.3em]">
                            {new Date(qrTime).toLocaleTimeString('es-AR', { hour12: false })} · Validando Seguridad...
                        </p>
                    </div>
                </div>

                {/* NAVBAR ACCIONES (DOCK STYLE) */}
                <div className="w-full p-4 bg-slate-50 dark:bg-[#0f172a] border-t border-slate-100 dark:border-slate-800 flex justify-around">
                    <button onClick={() => onNavigate('beneficios')} className="flex flex-col items-center p-3 text-slate-400 hover:text-[#0086CE] transition-all">
                        <span className="material-icons-outlined text-[28px]">local_activity</span>
                        <span className="text-[8px] font-black uppercase mt-1 tracking-widest">Beneficios</span>
                    </button>

                    <button onClick={() => document.documentElement.classList.toggle('dark')} className="flex flex-col items-center p-3 text-slate-400 hover:text-amber-500 transition-all">
                        <span className="material-icons-outlined text-[28px] dark:hidden">dark_mode</span>
                        <span className="material-icons-outlined text-[28px] hidden dark:block">light_mode</span>
                        <span className="text-[8px] font-black uppercase mt-1 tracking-widest">Tema</span>
                    </button>

                    <button onClick={() => onNavigate('login')} className="flex flex-col items-center p-3 text-slate-400 hover:text-red-500 transition-all">
                        <span className="material-icons-outlined text-[28px]">logout</span>
                        <span className="text-[8px] font-black uppercase mt-1 tracking-widest">Salir</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

function BeneficiosScreen({ onNavigate, addLog }) {
    const [prestadores, setPrestadores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterRubro, setFilterRubro] = useState('ALL');
    const [filterProvincia, setFilterProvincia] = useState('ALL');
    const [filterCercaMio, setFilterCercaMio] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [selectedPrestador, setSelectedPrestador] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Drag-to-scroll para slider destacados
    const sliderRef = React.useRef(null);
    const sliderDragging = React.useRef(false);
    const sliderStartX = React.useRef(0);
    const sliderStartScroll = React.useRef(0);
    const onSliderMouseDown = (e) => {
        sliderDragging.current = true;
        sliderStartX.current = e.pageX;
        sliderStartScroll.current = sliderRef.current.scrollLeft;
        sliderRef.current.style.cursor = 'grabbing';
        e.preventDefault();
    };
    const onSliderMouseUp = () => {
        sliderDragging.current = false;
        if (sliderRef.current) sliderRef.current.style.cursor = 'grab';
    };
    const onSliderMouseMove = (e) => {
        if (!sliderDragging.current || !sliderRef.current) return;
        const dx = e.pageX - sliderStartX.current;
        sliderRef.current.scrollLeft = sliderStartScroll.current - dx;
    };

    const scrollSlider = (direction) => {
        if (sliderRef.current) {
            const scrollAmount = direction === 'left' ? -300 : 300;
            sliderRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    const PRE_CATEGORIES = [
        "Alimentos y Bebidas",
        "Clínica / Sanatorio",
        "Deportes y Gimnasio",
        "Educación / Librería",
        "Especialidades Médicas",
        "Estética / Peluquería",
        "Estudios / Laboratorio",
        "Farmacia",
        "Gastronomía / Restaurantes",
        "Hogar / Decoración",
        "Indumentaria / Calzado",
        "Odontología",
        "Óptica",
        "Otro / Servicios",
        "Supermercado",
        "Turismo / Hotelería"
    ].sort();

    const PRE_PROVINCES = [
        "CABA", "Buenos Aires", "Catamarca", "Chaco", "Chubut", "Córdoba", "Corrientes",
        "Entre Ríos", "Formosa", "Jujuy", "La Pampa", "La Rioja", "Mendoza", "Misiones",
        "Neuquén", "Río Negro", "Salta", "San Juan", "San Luis", "Santa Cruz",
        "Santa Fe", "Santiago del Estero", "Tierra del Fuego", "Tucumán"
    ];

    const getCategoryIcon = (cat) => {
        const icons = {
            "Alimentos y Bebidas": "restaurant",
            "Clínica / Sanatorio": "local_hospital",
            "Deportes y Gimnasio": "fitness_center",
            "Educación / Librería": "menu_book",
            "Especialidades Médicas": "medical_services",
            "Estética / Peluquería": "content_cut",
            "Estudios / Laboratorio": "biotech",
            "Farmacia": "local_pharmacy",
            "Gastronomía / Restaurantes": "dinner_dining",
            "Hogar / Decoración": "home",
            "Indumentaria / Calzado": "checkroom",
            "Odontología": "teeth",
            "Óptica": "visibility",
            "Otro / Servicios": "more_horiz",
            "Supermercado": "shopping_cart",
            "Turismo / Hotelería": "hotel",
            "ALL": "home"
        };
        return icons[cat] || "storefront";
    };

    useEffect(() => {
        loadPrestadores();
    }, []);

    const loadPrestadores = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('prestadores')
                .select('*')
                .eq('estado', 'aprobado')
                .order('nombre_fantasia', { ascending: true });

            if (error) throw error;
            setPrestadores(data || []);
        } catch (err) {
            console.error(err);
            addLog('Error cargando beneficios: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Radio de la Tierra en km
        const dLat = (lat2 - lat1) * (Math.PI / 180);
        const dLon = (lon2 - lon1) * (Math.PI / 180);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    const handleCercaMio = () => {
        if (filterCercaMio) {
            setFilterCercaMio(false);
            return;
        }
        if (navigator.geolocation) {
            setLoading(true);
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                    setFilterCercaMio(true);
                    setLoading(false);
                },
                (err) => {
                    alert('No se pudo obtener la ubicación. Revisá los permisos del navegador.');
                    setLoading(false);
                }
            );
        } else {
            alert('Cerca Mío no está soportado en este navegador.');
        }
    };

    const filtered = prestadores.filter(p => {
        const matchesRubro = filterRubro === 'ALL' || p.rubro === filterRubro;
        const matchesProv = filterProvincia === 'ALL' || p.provincia === filterProvincia;

        const text = `${p.nombre_fantasia} ${p.rubro} ${p.descripcion_comercial} ${(p.etiquetas || []).join(' ')}`.toLowerCase();
        const matchesSearch = text.includes(searchQuery.toLowerCase());

        let distanceFilterPassed = true;
        if (filterCercaMio && userLocation && p.latitud && p.longitud) {
            const dist = calculateDistance(userLocation.lat, userLocation.lng, p.latitud, p.longitud);
            if (dist > 500) distanceFilterPassed = false;
            p._distance = dist;
        } else if (filterCercaMio && (!p.latitud || !p.longitud)) {
            distanceFilterPassed = false;
        }
        return matchesRubro && matchesProv && matchesSearch && distanceFilterPassed;
    }).sort((a, b) => {
        if (filterCercaMio && a._distance !== undefined && b._distance !== undefined) {
            return a._distance - b._distance;
        }
        return 0;
    });

    return (
        <div className="bg-background-light dark:bg-background-dark font-body min-h-screen flex flex-col">
            {/* Header Premium con Logo Animado */}
            <div className="sticky top-0 z-50 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-lg px-4 pt-6 pb-2">
                <div className="w-full max-w-md mx-auto flex items-center justify-between gap-4">
                    <div className="w-10 h-10 flex items-center justify-center">
                        <SupasoLogoAnimated size="small" />
                    </div>
                    <div className="flex-1 text-center">
                        <h2 className="text-xl font-[1000] text-supaso-dark dark:text-white uppercase tracking-tighter font-display leading-tight">Red de Beneficios</h2>
                        <p className="text-xs text-supaso-dark dark:text-supaso-primary font-brand uppercase tracking-[0.4em] leading-none mt-1">SUPASO</p>
                    </div>
                    <button onClick={() => onNavigate('credential')} className="w-10 h-10 rounded-full bg-white dark:bg-surface-dark shadow-sm text-supaso-dark dark:text-white flex items-center justify-center border border-slate-100 dark:border-slate-800 transition-all active:scale-90">
                        <span className="material-icons-round">arrow_back</span>
                    </button>
                </div>
            </div>

            {/* Buscador */}
            <div className="w-full max-w-md mx-auto px-4 mt-2 mb-4">
                <div className="relative flex items-center bg-white dark:bg-surface-dark rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 p-1">
                    <span className="material-icons-round ml-3 text-slate-400">search</span>
                    <input
                        type="text"
                        placeholder="¿Qué estás buscando hoy?"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="flex-1 px-4 py-3 bg-transparent border-none text-sm outline-none focus:ring-0 font-bold placeholder:text-slate-300 dark:text-white"
                    />
                    <button
                        onClick={handleCercaMio}
                        className={`flex flex-col sm:flex-row items-center gap-0.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl transition-all ${filterCercaMio ? 'bg-supaso-primary text-white shadow-lg' : 'bg-slate-50 text-supaso-primary border border-supaso-primary/20'}`}
                    >
                        <span className="material-icons-round text-xl">near_me</span>
                        <span className="text-[8px] sm:text-[9px] font-[1000] uppercase tracking-tighter whitespace-nowrap">Ver cerca mío</span>
                    </button>
                </div>
            </div>

            {/* Slider Destacados - Ancho Completo, sin restricción max-w */}
            {!filterCercaMio && searchQuery === '' && filterRubro === 'ALL' && prestadores.length > 0 && (
                <div style={{ width: '100%', marginBottom: '1.5rem', position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 1.25rem 0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <h4 style={{ fontSize: '10px', fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.15em', margin: 0 }}>Destacados</h4>
                            <div className="h-1 w-1 rounded-full bg-supaso-primary"></div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => scrollSlider('left')} className="w-6 h-6 rounded-full bg-white dark:bg-surface-dark shadow-sm flex items-center justify-center border border-slate-100 dark:border-slate-800 text-slate-400 active:scale-90 hover:text-supaso-primary transition-all">
                                <span className="material-icons-round text-sm">chevron_left</span>
                            </button>
                            <button onClick={() => scrollSlider('right')} className="w-6 h-6 rounded-full bg-white dark:bg-surface-dark shadow-sm flex items-center justify-center border border-slate-100 dark:border-slate-800 text-slate-400 active:scale-90 hover:text-supaso-primary transition-all">
                                <span className="material-icons-round text-sm">chevron_right</span>
                            </button>
                        </div>
                    </div>
                    <div
                        ref={sliderRef}
                        onMouseDown={onSliderMouseDown}
                        onMouseUp={onSliderMouseUp}
                        onMouseLeave={onSliderMouseUp}
                        onMouseMove={onSliderMouseMove}
                        style={{
                            display: 'flex',
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            gap: '1.25rem',
                            padding: '0.5rem 1.25rem 1.5rem',
                            scrollBehavior: 'smooth',
                            cursor: 'grab',
                            userSelect: 'none'
                        }}>
                        {prestadores.slice(0, 8).map(p => (
                            <div key={`feat-${p.id}`} onClick={() => setSelectedPrestador(p)}
                                style={{
                                    flex: '0 0 auto',
                                    width: '250px',
                                    minWidth: '250px',
                                    height: '160px',
                                    borderRadius: '1.5rem',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    boxShadow: '0 12px 40px rgba(1,33,96,0.15)',
                                    cursor: 'pointer'
                                }}>
                                {p.foto_portada_url
                                    ? <img src={p.foto_portada_url} alt="Portada" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                    : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#012160,#0086CE)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <span className="material-icons-round" style={{ fontSize: '3rem', color: 'rgba(255,255,255,0.15)' }}>{getCategoryIcon(p.rubro)}</span>
                                    </div>
                                }
                                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(1,33,96,0.92) 0%,rgba(1,33,96,0.1) 55%,transparent 100%)' }} />
                                <div style={{ position: 'absolute', bottom: '0.875rem', left: '1rem', right: '1rem' }}>
                                    <p style={{ fontSize: '8px', fontWeight: 900, color: '#0086CE', textTransform: 'uppercase', letterSpacing: '0.2em', marginBottom: '2px' }}>{p.rubro}</p>
                                    <h3 style={{ fontSize: '15px', fontWeight: 1000, color: 'white', lineHeight: 1.2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontFamily: 'Outfit, sans-serif', textTransform: 'uppercase' }}>{p.nombre_fantasia || p.razon_social}</h3>
                                    {p.beneficios && (
                                        <div style={{ marginTop: '5px', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.15)' }}>
                                            <span className="material-icons-round" style={{ fontSize: '10px', color: 'white' }}>auto_awesome</span>
                                            <span style={{ fontSize: '9px', fontWeight: 700, color: 'white', fontStyle: 'italic' }}>{p.beneficios}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Filtros + Listado */}
            <div className="w-full max-w-md mx-auto px-4 space-y-6 pb-24">

                {/* Filtros de Categorías Horizontales */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                        <h4 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Categorías</h4>
                        <button onClick={() => setFilterRubro('ALL')} className="text-[10px] font-black text-supaso-primary uppercase underline">Ver todas</button>
                    </div>
                    <div className="flex overflow-x-auto gap-4 pb-2 no-scrollbar px-1 snap-x">
                        {['ALL', ...PRE_CATEGORIES].map(c => (
                            <button
                                key={c}
                                onClick={() => setFilterRubro(c)}
                                className={`flex flex-col items-center gap-2 min-w-[70px] snap-center transition-all`}
                            >
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border-2 transition-all ${filterRubro === c ? 'bg-supaso-dark border-supaso-dark text-white shadow-xl scale-110' : 'bg-white dark:bg-surface-dark border-slate-100 dark:border-slate-800 text-slate-400 shadow-sm'}`}>
                                    <span className="material-icons-round text-2xl">{getCategoryIcon(c)}</span>
                                </div>
                                <span className={`text-[9px] font-black uppercase tracking-tighter text-center max-w-[64px] truncate ${filterRubro === c ? 'text-supaso-dark dark:text-supaso-primary' : 'text-slate-400'}`}>
                                    {c === 'ALL' ? 'Inicio' : c.split(' / ')[0]}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Filtro por Provincia (Compacto) */}
                <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
                    <button
                        onClick={() => setFilterProvincia('ALL')}
                        className={`px-4 py-2 rounded-xl whitespace-nowrap text-[9px] font-black uppercase tracking-widest transition-all ${filterProvincia === 'ALL' ? 'bg-supaso-primary text-white shadow-md' : 'bg-white dark:bg-surface-dark text-slate-400 border border-slate-100 dark:border-slate-800'}`}
                    >
                        Todas las Provincias
                    </button>
                    {PRE_PROVINCES.map(p => (
                        <button
                            key={p}
                            onClick={() => setFilterProvincia(p)}
                            className={`px-4 py-2 rounded-xl whitespace-nowrap text-[9px] font-black uppercase tracking-widest transition-all ${filterProvincia === p ? 'bg-supaso-primary text-white shadow-md' : 'bg-white dark:bg-surface-dark text-slate-400 border border-slate-100 dark:border-slate-800'}`}
                        >
                            {p}
                        </button>
                    ))}
                </div>

                {selectedPrestador && (
                    <PrestadorDetailModal
                        prestador={selectedPrestador}
                        onClose={() => setSelectedPrestador(null)}
                    />
                )}

                {/* Listado */}
                {/* Listado estilo Magazine */}
                <div className="grid grid-cols-1 gap-6 pb-20 mt-4">
                    {loading ? (
                        <div className="text-center p-12 text-slate-400">
                            <span className="material-icons animate-spin-slow text-4xl text-supaso-primary">autorenew</span>
                            <p className="mt-4 text-xs font-black uppercase tracking-[0.2em]">Buscando beneficios...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 px-8 text-center bg-white dark:bg-surface-dark rounded-[2.5rem] border-2 border-dashed border-slate-100 dark:border-slate-800 shadow-sm">
                            <div className="w-20 h-20 bg-slate-50 dark:bg-slate-900 rounded-full flex items-center justify-center mb-6 text-slate-300">
                                <span className="material-icons-round text-5xl">search_off</span>
                            </div>
                            <h3 className="text-xl font-[900] text-supaso-dark dark:text-white uppercase tracking-tighter">Sin coincidencias</h3>
                            <p className="text-sm text-slate-400 mt-2 max-w-[240px]">No encontramos prestadores con esos criterios. ¡Intenta con otra búsqueda!</p>
                            <button onClick={() => { setSearchQuery(''); setFilterRubro('ALL'); setFilterProvincia('ALL'); }} className="mt-8 px-8 py-3 bg-supaso-primary/10 text-supaso-primary rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-supaso-primary hover:text-white transition-all">Limpiar filtros</button>
                        </div>
                    ) : (
                        filtered.map(p => (
                            <div key={p.id} onClick={() => setSelectedPrestador(p)} className="group relative bg-white dark:bg-surface-dark rounded-[2.5rem] shadow-xl shadow-slate-200/40 dark:shadow-none border border-slate-100 dark:border-slate-800 flex flex-col overflow-hidden transition-all hover:-translate-y-1 active:scale-95 cursor-pointer">
                                {/* Portada con Badge de Beneficio */}
                                <div className="relative h-48 overflow-hidden">
                                    {p.foto_portada_url ? (
                                        <img src={p.foto_portada_url} alt="Portada" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-supaso-dark to-supaso-primary flex items-center justify-center opacity-80">
                                            <span className="material-icons-round text-6xl text-white/20">{getCategoryIcon(p.rubro)}</span>
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>

                                    {/* Badge Flotante de Beneficio */}
                                    {p.beneficios && (
                                        <div className="absolute top-4 right-4 bg-white shadow-xl rounded-2xl p-2 min-w-[60px] text-center border-t-4 border-supaso-primary animate-bounce-slow">
                                            <p className="text-[10px] font-black text-slate-400 uppercase leading-none mb-1">Beneficio</p>
                                            <p className="text-lg font-[900] text-supaso-dark leading-none truncate max-w-[80px] px-1">
                                                {p.beneficios.match(/\d+%/)?.[0] || 'Club'}
                                            </p>
                                        </div>
                                    )}

                                    <div className="absolute bottom-4 left-5 right-5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-xl bg-white p-1 shadow-lg ring-2 ring-white/20 shrink-0">
                                                <img src={p.logo_url || 'https://via.placeholder.com/100'} alt="Logo" className="w-full h-full object-contain rounded-lg" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[9px] font-black text-supaso-primary uppercase tracking-[0.2em] mb-0.5">{p.rubro}</p>
                                                <h3 className="text-xl font-[900] text-white truncate drop-shadow-md font-display leading-tight">{p.nombre_fantasia || p.razon_social}</h3>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Detalles Inferiores */}
                                <div className="p-5">
                                    {p.descripcion_comercial && (
                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed italic mb-3">
                                            "{p.descripcion_comercial}"
                                        </p>
                                    )}

                                    {/* Etiquetas restauradas en tarjeta */}
                                    {(p.etiquetas || []).length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mb-4">
                                            {p.etiquetas.slice(0, 3).map((tag, idx) => (
                                                <span key={idx} className="text-[8px] font-black uppercase bg-slate-50 dark:bg-slate-800 text-slate-400 px-2 py-0.5 rounded-md border border-slate-100 dark:border-slate-700">
                                                    #{tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between mt-auto">
                                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 dark:bg-slate-800 rounded-full border border-slate-100 dark:border-slate-700">
                                            <span className="material-icons-round text-[14px] text-slate-400">my_location</span>
                                            <span className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-tighter">
                                                {p._distance !== undefined ? `${p._distance.toFixed(1)} km - ${p.localidad}` : p.localidad}
                                            </span>
                                        </div>
                                        <div className="w-10 h-10 rounded-full bg-supaso-primary text-white flex items-center justify-center shadow-lg shadow-supaso-primary/20 group-hover:bg-supaso-dark transition-colors">
                                            <span className="material-icons-round text-xl">arrow_forward</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

function PrestadorDetailModal({ prestador, onClose }) {
    const images = [prestador.foto_portada_url, ...(prestador.galeria_urls || [])].filter(Boolean);
    const galleryRef = React.useRef(null);
    const isDragging = React.useRef(false);
    const startX = React.useRef(0);
    const startScroll = React.useRef(0);
    const [currentImg, setCurrentImg] = useState(0);

    const nextImg = (e) => { e.stopPropagation(); if (currentImg < images.length - 1) setCurrentImg(currentImg + 1); };
    const prevImg = (e) => { e.stopPropagation(); if (currentImg > 0) setCurrentImg(currentImg - 1); };

    useEffect(() => {
        if (galleryRef.current) {
            galleryRef.current.scrollTo({
                left: currentImg * galleryRef.current.offsetWidth,
                behavior: 'smooth'
            });
        }
    }, [currentImg]);

    const onGalleryMouseDown = (e) => {
        isDragging.current = true;
        startX.current = e.pageX;
        startScroll.current = galleryRef.current.scrollLeft;
        galleryRef.current.style.cursor = 'grabbing';
    };
    const onGalleryMouseUp = (e) => {
        if (!isDragging.current) return;
        isDragging.current = false;
        galleryRef.current.style.cursor = 'grab';

        // Determinar si movemos al siguiente/anterior basado en cuánto arrastró
        const dx = e.pageX - startX.current;
        if (Math.abs(dx) > 50) {
            if (dx < 0 && currentImg < images.length - 1) setCurrentImg(currentImg + 1);
            else if (dx > 0 && currentImg > 0) setCurrentImg(currentImg - 1);
        } else {
            // Volver a la imagen actual
            galleryRef.current.scrollTo({
                left: currentImg * galleryRef.current.offsetWidth,
                behavior: 'smooth'
            });
        }
    };
    const onGalleryMouseMove = (e) => {
        if (!isDragging.current || !galleryRef.current) return;
        const dx = e.pageX - startX.current;
        galleryRef.current.scrollLeft = startScroll.current - dx;
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-supaso-dark/90 backdrop-blur-xl transition-all" onClick={onClose}>
            <div className="bg-white dark:bg-surface-dark w-full max-w-lg rounded-t-[3rem] sm:rounded-[2.5rem] shadow-2xl animate-slide-up flex flex-col max-h-[95vh] relative border-t-8 border-supaso-primary sm:border-t-0" style={{ overflowY: 'auto' }} onClick={e => e.stopPropagation()}>

                {/* Galería con drag-to-scroll */}
                <div style={{ position: 'relative', flexShrink: 0, height: '18rem', background: '#0f172a', borderTopLeftRadius: '3rem', borderTopRightRadius: '3rem' }}>
                    <div
                        ref={galleryRef}
                        onMouseDown={onGalleryMouseDown}
                        onMouseUp={onGalleryMouseUp}
                        onMouseLeave={onGalleryMouseUp}
                        onMouseMove={onGalleryMouseMove}
                        style={{
                            display: 'flex',
                            height: '100%',
                            overflowX: 'hidden',
                            scrollSnapType: 'none',
                            WebkitOverflowScrolling: 'touch',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            cursor: images.length > 1 ? 'grab' : 'default',
                            userSelect: 'none'
                        }}
                    >
                        {images.length > 0 ? images.map((url, i) => (
                            <div key={i} style={{ flex: '0 0 100%', minWidth: '100%', width: '100%', height: '100%', scrollSnapAlign: 'center', pointerEvents: 'none' }}>
                                <img src={url} alt={`Foto ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            </div>
                        )) : (
                            <div style={{ flex: '0 0 100%', minWidth: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #012160, #0086CE)' }}>
                                <span className="material-icons-round" style={{ fontSize: '5rem', color: 'rgba(255,255,255,0.1)' }}>storefront</span>
                            </div>
                        )}
                    </div>

                    {/* Branding SUPASO */}
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '1.25rem', display: 'flex', alignItems: 'flex-start', pointerEvents: 'none', zIndex: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderRadius: '14px', background: 'rgba(255,255,255,0.93)', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '1px solid rgba(255,255,255,0.3)' }}>
                            <img src="https://supaso.org/logo.png" alt="SUPASO" style={{ width: 18, height: 18, objectFit: 'contain' }} />
                            <span style={{ fontFamily: '"Russo One", sans-serif', fontSize: '10px', color: '#012160', textTransform: 'uppercase', letterSpacing: '0.3em', lineHeight: 1 }}>SUPASO</span>
                        </div>
                    </div>

                    {/* Overlay gradiente inferior */}
                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(to top, white 0%, transparent 55%)', zIndex: 1 }}></div>

                    {/* Botón Cerrar */}
                    <button
                        onClick={onClose}
                        style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, cursor: 'pointer', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}
                    >
                        <span className="material-icons-round" style={{ fontSize: '20px' }}>close</span>
                    </button>

                    {/* Botones de Navegación Arrows */}
                    {images.length > 1 && (
                        <>
                            <button
                                onClick={prevImg}
                                style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(12px)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 110, cursor: currentImg === 0 ? 'default' : 'pointer', opacity: currentImg === 0 ? 0 : 1, transition: 'all 0.3s', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
                            >
                                <span className="material-icons-round" style={{ fontSize: '28px' }}>chevron_left</span>
                            </button>
                            <button
                                onClick={nextImg}
                                style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(12px)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 110, cursor: currentImg === images.length - 1 ? 'default' : 'pointer', opacity: currentImg === images.length - 1 ? 0 : 1, transition: 'all 0.3s', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
                            >
                                <span className="material-icons-round" style={{ fontSize: '28px' }}>chevron_right</span>
                            </button>
                        </>
                    )}

                    {/* Indicador de posición visual (bolitas) */}
                    {images.length > 1 && (
                        <div style={{ position: 'absolute', bottom: '1.5rem', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '6px', zIndex: 40 }}>
                            {images.map((_, i) => (
                                <div key={i} style={{ width: i === currentImg ? '20px' : '6px', height: '6px', borderRadius: '10px', background: i === currentImg ? '#0086CE' : 'rgba(255,255,255,0.5)', transition: 'all 0.3s' }}></div>
                            ))}
                        </div>
                    )}

                    {/* Indicador deslizar (Badge flotante) */}
                    {images.length > 1 && (
                        <div style={{ position: 'absolute', bottom: '4rem', right: '1rem', background: '#012160', color: 'white', padding: '7px 12px', borderRadius: '12px', fontSize: '9px', fontWeight: 900, letterSpacing: '0.15em', textTransform: 'uppercase', zIndex: 10, border: '2px solid rgba(0,134,206,0.5)', display: 'flex', alignItems: 'center', gap: '6px', pointerEvents: 'none' }}>
                            <span className="material-icons-round" style={{ fontSize: '15px' }}>swipe</span>
                            {currentImg + 1} / {images.length} fotos
                        </div>
                    )}
                </div>



                <div className="relative -mt-10 px-6 pb-8 overflow-y-auto custom-scrollbar flex-1" style={{ zIndex: 5 }}>
                    {/* Cabezal de Info */}
                    <div className="flex items-end gap-4 mb-8">
                        <div className="w-24 h-24 rounded-3xl bg-white dark:bg-slate-700 p-1 shadow-2xl shrink-0 overflow-hidden" style={{ border: '4px solid rgba(0,134,206,0.4)' }}>
                            <img src={prestador.logo_url || 'https://via.placeholder.com/150'} alt="Logo" className="w-full h-full object-contain rounded-2xl" />
                        </div>
                        <div className="pb-2 min-w-0">
                            <p className="text-[10px] font-black text-supaso-primary uppercase tracking-widest mb-1 leading-none">{prestador.rubro}</p>
                            <h3 className="text-2xl font-[1000] text-supaso-dark dark:text-white leading-none truncate font-display uppercase tracking-tighter">{prestador.nombre_fantasia || prestador.razon_social}</h3>
                        </div>
                    </div>

                    {/* Tarjeta de Beneficio Destacada */}
                    {prestador.beneficios && (
                        <div className="bg-gradient-to-br from-supaso-primary to-blue-700 p-6 rounded-[2rem] shadow-xl shadow-supaso-primary/20 mb-8 relative overflow-hidden group">
                            <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform"></div>
                            <div className="relative flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                                    <span className="material-icons-round text-white text-2xl">auto_awesome</span>
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-[10px] font-black text-white/70 uppercase tracking-widest mb-1">Red de Beneficios</h4>
                                    <p className="text-xl font-[900] text-white leading-tight italic">"{prestador.beneficios}"</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Descripción y Vigencia */}
                    <div className="space-y-6 mb-8">
                        {prestador.descripcion_comercial && (
                            <div>
                                <h4 className="text-[12px] font-[1000] text-supaso-dark dark:text-white uppercase tracking-[0.1em] mb-4 flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-supaso-primary/10 flex items-center justify-center">
                                        <span className="material-icons-round text-2xl text-supaso-primary">storefront</span>
                                    </div>
                                    Información del Comercio
                                </h4>
                                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed font-medium bg-slate-50 dark:bg-slate-800/50 p-5 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-inner">
                                    {prestador.descripcion_comercial}
                                </p>
                            </div>
                        )}

                        {/* Etiquetas restauradas en modal */}
                        {(prestador.etiquetas || []).length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {prestador.etiquetas.map((tag, idx) => (
                                    <span key={idx} className="text-[10px] font-black uppercase bg-white dark:bg-surface-dark text-slate-400 px-3 py-1 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm">
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {(prestador.vigencia_inicio || prestador.vigencia_fin) && (
                                <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                                    <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-700 shadow-sm flex items-center justify-center text-amber-500">
                                        <span className="material-icons-round">event_available</span>
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-black text-slate-400 uppercase">Vigencia</p>
                                        <p className="text-xs font-bold text-supaso-dark dark:text-slate-200">
                                            {prestador.vigencia_fin ? `Hasta ${new Date(prestador.vigencia_fin).toLocaleDateString('es-AR')}` : 'Activo'}
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                                <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-700 shadow-sm flex items-center justify-center text-supaso-primary">
                                    <span className="material-icons-round">pin_drop</span>
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[9px] font-black text-slate-400 uppercase">Ubicación</p>
                                    <p className="text-[10px] font-bold text-supaso-dark dark:text-slate-200 truncate">{prestador.direccion}</p>
                                    <p className="text-[9px] text-slate-500">{prestador.localidad}, {prestador.provincia}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Botones de Acción */}
                    <div className="space-y-3">
                        {prestador.telefono && (
                            <a href={`https://wa.me/${prestador.telefono.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white py-4 rounded-2xl flex items-center justify-center gap-3 font-black shadow-lg shadow-green-500/20 transition-all active:scale-95 text-sm uppercase tracking-widest">
                                <span className="material-icons-round text-xl">whatsapp</span> Contactar WhatsApp
                            </a>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((prestador.direccion || '') + ', ' + (prestador.localidad || '') + ', ' + (prestador.provincia || ''))}`} target="_blank" rel="noreferrer" className="bg-white dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 py-4 rounded-2xl flex items-center justify-center gap-2 text-supaso-dark dark:text-white font-black text-[10px] uppercase tracking-widest active:scale-95 transition-all">
                                <span className="material-icons-round text-lg text-red-500">directions</span> Mapas
                            </a>
                            {prestador.instagram ? (
                                <a href={`https://instagram.com/${prestador.instagram.replace('@', '')}`} target="_blank" rel="noreferrer" className="bg-white dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 py-4 rounded-2xl flex items-center justify-center gap-2 text-supaso-dark dark:text-white font-black text-[10px] uppercase tracking-widest active:scale-95 transition-all">
                                    <span className="material-icons-round text-lg text-pink-500">camera_alt</span> Instagram
                                </a>
                            ) : (
                                <div className="bg-slate-50 dark:bg-slate-800/50 py-4 rounded-2xl flex items-center justify-center gap-2 text-slate-300 font-black text-[10px] uppercase tracking-widest opacity-50">
                                    <span className="material-icons-round text-lg">public</span> Web
                                </div>
                            )}
                        </div>

                        {prestador.telefono_fijo && (
                            <a href={`tel:${prestador.telefono_fijo.replace(/\D/g, '')}`} className="w-full bg-supaso-dark text-white py-4 rounded-2xl flex items-center justify-center gap-3 font-black shadow-lg transition-all active:scale-95 text-[10px] uppercase tracking-widest">
                                <span className="material-icons-round text-xl">call</span> Llamar a Línea ({prestador.telefono_fijo})
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}


